import * as React from "react";
import { Button } from "../../Button";
import { z } from "zod";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Option, Dialog, DialogActions, DialogContent, DialogTitleWithCloseButton, ConfirmationDialog } from "../../../../shared/components";

interface UnlinkDialogProps {
    entityName: string;
    entities: Option[];
    acceptCallback: (args: Object) => void;
    closeCallback: () => void;
}

function UnlinkDialog(props: UnlinkDialogProps) {
    const { entityName, entities, acceptCallback, closeCallback } = props;

    const schema = z.object({
        entity: z.object({
            value: z.string(),
            label: z.string()
        }, {
            required_error: `Please select a ${entityName} to unlink.`
        })
    });
    type FormValues = z.infer<typeof schema>;

    const { handleSubmit, control } = useForm({ resolver: zodResolver(schema), defaultValues: { entity: null } });

    const [onClearCallback, setOnClearCallback] = React.useState(null);
    const [confirmationLabel, setConfirmationLabel] = React.useState("");
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        const onClearCallback = () => acceptCallback({
            entity_id: formModel.entity.value,
            entity_label: formModel.entity.label
        });
        setOnClearCallback(() => onClearCallback);
        setConfirmationLabel(formModel.entity.label);
        setShowConfirmation(true);
    };

    return (
        <>
            <Dialog
                open={true}
                onClose={(_event, reason) => {
                    if (reason !== 'backdropClick') {
                        closeCallback();
                    }
                }}
                disablePortal={true}
                aria-labelledby="form-dialog-title">
                <DialogTitleWithCloseButton onClose={closeCallback} id="form-dialog-title">
                    Unlink {entityName}
                </DialogTitleWithCloseButton>
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <DialogContent>
                        <div className="vertical-form-field">
                            Select {entityName} to unlink:
                        </div>
                        <Controller
                            name="entity"
                            control={control}
                            render={({
                                field: { value, onChange },
                                fieldState: { error }
                            }) => (
                                <Autocomplete
                                    value={value}
                                    onChange={(_event, option, _reason) => onChange((option as Option))}
                                    error={!!error}
                                    helperText={error?.message}
                                    isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                    getOptionLabel={(option: any) => option?.label || ""}
                                    size="small"
                                    fullWidth
                                    options={entities}
                                    disableClearable
                                />
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="text" color="warning">
                            Unlink
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {
                showConfirmation &&
                <ConfirmationDialog
                    title={`Unlink ${entityName}?`}
                    open={showConfirmation}
                    setOpen={setShowConfirmation}
                    onConfirm={onClearCallback}
                >
                    <h3>Warning:</h3>
                    <br />
                    This operation will unlink '{confirmationLabel}'.  Changes in the linked Base Case will no longer reflect in this {entityName} once the {entityName} is unlinked.  This cannot be reversed!
                    <br />
                    <br />
                </ConfirmationDialog>
            }
        </>
    );
}

export { UnlinkDialog };